import { createListLinkTarget } from '../../utils/listPageState'

const EDIT_APP_COUPANG_BANNER_DRAFT_PREFIX = 'admin:appCoupangBannerDraft:edit:'
export const NEW_APP_COUPANG_BANNER_DRAFT_KEY = 'admin:appCoupangBannerDraft:new'
export const APP_COUPANG_BANNER_IMAGE_FILE_ERROR_MESSAGE = '이미지 파일만 업로드할 수 있습니다.'
export const APP_COUPANG_BANNER_IMAGE_FILE_ACCEPT = '.jpg,.jpeg,.png,.gif,.webp,image/png,image/gif,image/jpeg,image/webp'
export const APP_COUPANG_BANNER_IMAGE_FIELD_CONFIGS = [
    {
        fieldName: 'image',
        inputId: 'image',
        previewAlt: '배너 이미지',
        uploadLabel: '배너 이미지 업로드',
    },
]

const getSessionStorage = () => {
    if (typeof window === 'undefined') {
        return null
    }

    return window.sessionStorage
}

const normalizeTextValue = (value) => {
    if (value === undefined || value === null) {
        return ''
    }

    return String(value)
}

const normalizeNumberValue = (value, fallbackValue) => {
    if (value === undefined || value === null || value === '') {
        return fallbackValue
    }

    const parsedValue = Number(value)

    return Number.isNaN(parsedValue) ? fallbackValue : parsedValue
}

export const createDefaultAppCoupangBannerParams = () => ({
    coupangId: null,
    productName: '',
    image: '',
    order: 99,
})

// 최저가 배너 폼은 이미지 파일을 제외하고 선택 상품과 상품 검색 상태만 sessionStorage에 남긴다.
export const createAppCoupangBannerDraftPayload = ({
    params = {},
    pickerPage = 1,
    pickerSearchText = '',
} = {}) => {
    return {
        params: {
            coupangId: params.coupangId ?? null,
            productName: normalizeTextValue(params.productName),
            order: normalizeNumberValue(params.order, 99),
        },
        pickerPage: normalizeNumberValue(pickerPage, 1),
        pickerSearchText: normalizeTextValue(pickerSearchText),
    }
}

// 서버 기본값 위에 draft만 덮어써서 새로고침 뒤에도 상품 선택 흐름을 이어간다.
export const applyAppCoupangBannerDraft = ({
    baseParams = {},
    basePickerPage = 1,
    basePickerSearchText = '',
    draft,
} = {}) => {
    const normalizedBaseDraft = createAppCoupangBannerDraftPayload({
        params: baseParams,
        pickerPage: basePickerPage,
        pickerSearchText: basePickerSearchText,
    })

    if (!draft) {
        return {
            params: {
                ...baseParams,
                ...normalizedBaseDraft.params,
            },
            pickerPage: normalizedBaseDraft.pickerPage,
            pickerSearchText: normalizedBaseDraft.pickerSearchText,
        }
    }

    const draftParams = draft.params || {}

    return {
        params: {
            ...baseParams,
            coupangId: draftParams.coupangId === undefined
                ? normalizedBaseDraft.params.coupangId
                : draftParams.coupangId,
            productName: draftParams.productName === undefined
                ? normalizedBaseDraft.params.productName
                : normalizeTextValue(draftParams.productName),
            order: draftParams.order === undefined
                ? normalizedBaseDraft.params.order
                : normalizeNumberValue(draftParams.order, normalizedBaseDraft.params.order),
        },
        pickerPage: draft.pickerPage === undefined
            ? normalizedBaseDraft.pickerPage
            : normalizeNumberValue(draft.pickerPage, normalizedBaseDraft.pickerPage),
        pickerSearchText: draft.pickerSearchText === undefined
            ? normalizedBaseDraft.pickerSearchText
            : normalizeTextValue(draft.pickerSearchText),
    }
}

export const applyAppCoupangBannerFieldChange = ({ params = {}, name, value }) => {
    if (!name) {
        return params
    }

    return {
        ...params,
        [name]: value,
    }
}

// 등록/수정 화면이 같은 필수값 검증과 FormData 구성을 사용하도록 정리한다.
export const prepareAppCoupangBannerSubmit = ({
    params = {},
    coupangBannerId,
    requireImage = true,
} = {}) => {
    if (!params.coupangId) {
        return {
            nextParams: params,
            errorMessage: '상품을 선택하세요.',
        }
    }

    if (requireImage && !params.image) {
        return {
            nextParams: params,
            errorMessage: '배너 이미지를 등록해주세요.',
        }
    }

    const nextOrder = Number(params.order)

    if (params.order === '' || Number.isNaN(nextOrder) || nextOrder < 0 || nextOrder > 99) {
        return {
            nextParams: params,
            errorMessage: '노출순서를 0~99 숫자로 입력하세요.',
        }
    }

    const nextParams = {
        ...params,
        order: nextOrder,
    }

    const formData = new FormData()

    if (coupangBannerId !== undefined && coupangBannerId !== null && coupangBannerId !== '') {
        formData.append('coupangBannerId', coupangBannerId)
    }

    formData.append('coupangId', nextParams.coupangId)
    formData.append('image', nextParams.image)
    formData.append('order', nextParams.order)

    return {
        nextParams,
        formData,
        errorMessage: '',
    }
}

export const createAppCoupangBannerListLinkTarget = ({
    listSearch = '',
    snackbarState,
    curPage,
    searchText,
    searchSelect,
}) => createListLinkTarget({
    pathname: '/appCoupangBanners',
    search: listSearch,
    curPage,
    searchText,
    searchSelect,
    ...(snackbarState ? {
        openSnackbar: true,
        severitySnackbar: snackbarState.severity,
        messageSnackbar: snackbarState.message,
    } : {}),
})

// 저장/취소 후 복귀 시 기존 목록 필터와 페이지를 유지하는 규칙을 한 곳에서 맞춘다.
export const moveToAppCoupangBannerList = ({
    draftKey,
    clearDraft,
    setNavigationUnlocked,
    history,
    listSearch,
    snackbarState,
    curPage,
    searchText,
    searchSelect,
}) => {
    setNavigationUnlocked(true)
    clearDraft(draftKey)

    setTimeout(() => {
        history.push(createAppCoupangBannerListLinkTarget({
            listSearch,
            snackbarState,
            curPage,
            searchText,
            searchSelect,
        }))
    }, 0)
}

export const handleAppCoupangBannerBackNavigation = ({
    event,
    leaveGuardEnabled,
    draftKey,
    clearDraft,
    confirmLeave,
    moveToList,
}) => {
    if (!leaveGuardEnabled) {
        clearDraft(draftKey)
        return
    }

    event.preventDefault()

    if (!confirmLeave()) {
        return
    }

    moveToList()
}

const isAppCoupangBannerImageFile = (file) => {
    return Boolean(file) && (
        (file.type && file.type.startsWith('image/')) ||
        /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name || '')
    )
}

export const handleAppCoupangBannerImageUpload = ({
    event,
    setParams,
    setImage,
    onInvalidFile,
}) => {
    event.preventDefault()

    const input = event.target
    const file = input.files?.[0]

    if (!file) {
        return
    }

    if (!isAppCoupangBannerImageFile(file)) {
        input.value = ''
        onInvalidFile(APP_COUPANG_BANNER_IMAGE_FILE_ERROR_MESSAGE)
        return
    }

    const reader = new FileReader()

    reader.onloadend = () => {
        setParams((prev) => ({
            ...prev,
            [input.name]: file,
        }))

        setImage(reader.result)
    }

    reader.readAsDataURL(file)
}

export const createEditAppCoupangBannerDraftKey = (coupangBannerId) => `${EDIT_APP_COUPANG_BANNER_DRAFT_PREFIX}${coupangBannerId}`

export const readAppCoupangBannerDraft = (draftKey) => {
    const storage = getSessionStorage()

    if (!storage || !draftKey) {
        return null
    }

    try {
        const storedValue = storage.getItem(draftKey)
        return storedValue ? JSON.parse(storedValue) : null
    } catch (_error) {
        return null
    }
}

export const saveAppCoupangBannerDraft = (draftKey, draftValue) => {
    const storage = getSessionStorage()

    if (!storage || !draftKey) {
        return
    }

    try {
        storage.setItem(draftKey, JSON.stringify(draftValue))
    } catch (_error) {
        // sessionStorage가 막혀 있어도 작성 흐름은 그대로 유지한다.
    }
}

export const clearAppCoupangBannerDraft = (draftKey) => {
    const storage = getSessionStorage()

    if (!storage || !draftKey) {
        return
    }

    try {
        storage.removeItem(draftKey)
    } catch (_error) {
        // storage 정리에 실패해도 화면 동작은 계속 진행한다.
    }
}
